import { useEffect, useState, useRef } from 'react'
import { getRequest, postRequest } from '../../services/base'
import { getLoginConfig } from '../login/api'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  deleteLocalStorage,
  setLocalStorageData,
} from '../../utils/LocalStorageAccess'
import { AUTH_TOKEN, MAKE_ID } from '../../constants/common'
import { isAuth } from '../../utils/auth'
import { clearHostedLoginBreadcrumb, isInAppPath, redirectToHostedLogin } from '../../utils/hostedLogin'
import { deleteSessionStorage } from '../../utils/SessionStorage'
import { useIntl } from 'react-intl'
import { Button } from 'antd'
import { CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import ErrorCard from '../../components/common/ErrorCard'

// ErrorCard takes an icon component; this one spins
const Spinning = (props) => <LoadingOutlined spin {...props} />

const Auth = () => {
  const intl = useIntl()
  // null while the sign-in is still being verified; the message once it failed
  const [error, setError] = useState(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const effectRan = useRef(false)
  const code = searchParams.get('code')

  useEffect(() => {
    if (effectRan.current) return
    effectRan.current = true

    if (code) {
      generateToken(code)
    } else {
      getAuthUrl()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  const getAuthUrl = async () => {
    // A host on AT's own login page signs in there, never on the hosted UI -
    // including after a provider sends the user back here without a code.
    const config = await getLoginConfig()
    if (config?.mode === 'cognito' && config?.custom_login) {
      navigate('/users/sign_in')
      return
    }
    let res = await getRequest('/generate_auth_redirect_url/', isAuth())
    if (isInAppPath(res?.redirect_url)) {
      window.location.href = res.redirect_url
    } else if (res?.redirect_url && redirectToHostedLogin(res.redirect_url)) {
      // On the way to the hosted login page
    } else {
      setError(intl.formatMessage({ id: 'auth.redirectFailed' }))
      // native_sign_in is set on exactly one branch of the endpoint - the one
      // that hands sign-in back to AT.
      if (res?.native_sign_in) {
        navigate('/users/sign_in')
      } else {
        alert(intl.formatMessage({ id: 'auth.redirectFailed' }))
      }
    }
  }

  const generateToken = async (code) => {
    let res = await postRequest('/generate_oauth_token/', { code: code })
    if (res?.success) {
      clearHostedLoginBreadcrumb()
      setLocalStorageData(AUTH_TOKEN, res.token)
      setLocalStorageData(MAKE_ID, res.make)
      window.location.href = '/vehicles'
    } else {
      deleteLocalStorage(AUTH_TOKEN)
      deleteLocalStorage(MAKE_ID)
      deleteSessionStorage('vehicle')
      deleteSessionStorage('dtcs_list')
      setError(intl.formatMessage({ id: 'auth.verifyFailed' }))
    }
  }
  // Laid out like the other signed-out pages (Logged Out, Not Permitted)
  return error ? (
    <div className="page auth-callback" data-testid="auth-failed">
      <ErrorCard
        icon={CloseCircleOutlined}
        iconStyle={{ color: 'var(--color-form-error-broder, #FF4D4F)' }}
        title={intl.formatMessage({ id: 'auth.signInFailedTitle' })}
        sub_title={error}
        extra={
          <Button onClick={() => navigate('/users/sign_in')} data-testid="auth-back-to-sign-in">
            {intl.formatMessage({ id: 'auth.backToSignIn' })}
          </Button>
        }
      />
    </div>
  ) : (
    <div className="page auth-callback" data-testid="auth-verifying">
      <ErrorCard
        icon={Spinning}
        iconStyle={{ color: 'inherit' }}
        title={intl.formatMessage({ id: 'auth.verifyingTitle' })}
        sub_title={intl.formatMessage({ id: 'auth.verifying' })}
      />
    </div>
  )
}

export default Auth
